import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavMenu } from '@shopify/app-bridge-react';
import { useShop } from '../context/ShopContext';

/**
 * App shell. `<NavMenu>` renders the left-hand navigation inside Shopify Admin.
 * The `rel="home"` link is the App Bridge default route. We intercept clicks so
 * React Router handles navigation client-side.
 *
 * Legacy (Gadget-migrated) shops get a reduced Quote-only navigation; new shops
 * get the full Request → Quote → Approval experience.
 */
export default function Layout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { shop } = useShop();
  const legacy = shop?.isLegacyShop ?? false;

  const link = (to: string, label: string, home = false) => (
    <a
      key={to}
      href={to}
      rel={home ? 'home' : undefined}
      aria-current={location.pathname === to ? 'page' : undefined}
      onClick={(e) => {
        e.preventDefault();
        navigate(to);
      }}
    >
      {label}
    </a>
  );

  return (
    <>
      <NavMenu>
        {legacy
          ? [link('/', 'Dashboard', true), link('/quotes', 'Quote Request'), link('/settings', 'Settings')]
          : [
              link('/', 'Dashboard', true),
              link('/requests', 'Requests'),
              link('/quotes', 'Quotes'),
              link('/companies', 'Accounts'),
              link('/configuration', 'Configuration'),
              link('/settings', 'Settings'),
            ]}
      </NavMenu>
      {children}
    </>
  );
}
